import { useEffect, useRef, useState } from 'react';
import { getLotRepresentativeLatLng } from './useLotSelection';
import { LOT_PIN_STYLE } from '../styles/mapTheme';
import { moveReferencePlacesForeground } from '../utils/referencePlaces';

const DEFAULT_PREFIX = 'list-pins';

// Zoom en el que los pins empiezan a desvanecerse (las parcelas PMTiles
// ya son visibles alrededor de MAP_MIN_LOT_ZOOM = 12).
const FADE_START_ZOOM = 12.5;
const FADE_END_ZOOM = 14.0;

/**
 * Espera a que el estilo de MapLibre esté listo. Cubre:
 * - mapa aún cargando (`load` / `idle`)
 * - mapa ya cargado pero `isStyleLoaded()` era false en el primer tick
 * - recarga de estilo (`styledata`) que elimina sources/layers
 */
export const whenMapStyleReady = (map, callback) => {
  if (!map) return () => {};

  let cancelled = false;

  const run = () => {
    if (cancelled) return true;
    try {
      if (!map.isStyleLoaded()) return false;
      callback();
      return true;
    } catch (_) {
      return false;
    }
  };

  if (run()) return () => {
    cancelled = true;
  };

  const tryRun = () => {
    if (run()) unbind();
  };

  const unbind = () => {
    map.off('load', tryRun);
    map.off('idle', tryRun);
    map.off('styledata', tryRun);
  };

  map.on('load', tryRun);
  map.on('idle', tryRun);
  map.on('styledata', tryRun);

  return () => {
    cancelled = true;
    unbind();
  };
};

/**
 * Agrega/actualiza una capa GeoJSON de puntos en MapLibre para los lotes
 * resaltados. Se hace visible a zoom bajo y desaparece gradualmente cuando
 * las parcelas PMTiles ya son visibles.
 *
 * @param {object}                 options
 * @param {React.MutableRefObject} options.mapInstanceRef
 * @param {Array}                  options.lots    — filas de getMyLots con lat/lng
 * @param {boolean}                options.enabled
 * @param {string}                 [options.idPrefix] — prefijo para IDs (source/layers)
 * @param {'solid'|'outline'}      [options.variant] — solid = miembros; outline = pendientes
 */
export function useListPinsLayer({
  mapInstanceRef,
  lots,
  enabled,
  idPrefix = DEFAULT_PREFIX,
  variant = 'solid',
}) {
  const mapRef = useRef(null);
  /** Fuerza re-sync cuando el mapa se asigna al ref (el ref en sí no re-renderiza). */
  const [mapAttachVersion, setMapAttachVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const map = mapInstanceRef.current;
    if (map) {
      setMapAttachVersion((v) => v + 1);
      return undefined;
    }

    let attempts = 0;
    const maxAttempts = 120;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (mapInstanceRef.current) {
        setMapAttachVersion((v) => v + 1);
        window.clearInterval(intervalId);
        return;
      }
      if (attempts >= maxAttempts) window.clearInterval(intervalId);
    }, 50);

    return () => window.clearInterval(intervalId);
  }, [enabled, mapInstanceRef]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    mapRef.current = map;

    if (!map || !enabled) {
      removeLayers(map, idPrefix);
      return undefined;
    }

    const geojson = buildGeoJSON(lots);
    const ids = getIds(idPrefix);
    const isOutline = variant === 'outline';
    const dotRadius = isOutline
      ? ['interpolate', ['linear'], ['zoom'], 6, 4.5, FADE_START_ZOOM, 6.5]
      : LOT_PIN_STYLE.circleRadius;

    const applyDotPaint = () => {
      map.setPaintProperty(ids.layerDotsId, 'circle-radius', dotRadius);
      map.setPaintProperty(ids.layerDotsId, 'circle-color', LOT_PIN_STYLE.color);
      map.setPaintProperty(ids.layerDotsId, 'circle-stroke-color', LOT_PIN_STYLE.strokeColor);
      map.setPaintProperty(ids.layerDotsId, 'circle-stroke-width', isOutline ? 2 : LOT_PIN_STYLE.strokeWidth);
      map.setPaintProperty(ids.layerDotsId, 'circle-opacity', [
        'interpolate', ['linear'], ['zoom'],
        FADE_START_ZOOM, isOutline ? 0.55 : LOT_PIN_STYLE.circleOpacity,
        FADE_END_ZOOM, 0,
      ]);
      map.setPaintProperty(ids.layerDotsId, 'circle-stroke-opacity', [
        'interpolate', ['linear'], ['zoom'],
        FADE_START_ZOOM, 0.95,
        FADE_END_ZOOM, 0,
      ]);
    };

    const upsertLayers = () => {
      if (!map.isStyleLoaded()) return;

      const hasSource = !!map.getSource(ids.sourceId);
      const hasDots = !!map.getLayer(ids.layerDotsId);
      const hasHalos = !!map.getLayer(ids.layerHalosId);

      if (hasHalos) {
        try { map.removeLayer(ids.layerHalosId); } catch (_) { /* capa huérfana */ }
      }

      if (hasSource && hasDots) {
        map.getSource(ids.sourceId).setData(geojson);
        applyDotPaint();
        moveReferencePlacesForeground(map);
        try {
          if (map.getLayer(ids.layerDotsId)) map.moveLayer(ids.layerDotsId);
        } catch (_) { /* estilo a medias */ }
        return;
      }

      // Source/layers a medias (p. ej. styledata): recrear limpio.
      if (hasSource || hasDots) {
        removeLayers(map, idPrefix);
      }

      map.addSource(ids.sourceId, { type: 'geojson', data: geojson });

      map.addLayer({
        id: ids.layerDotsId,
        type: 'circle',
        source: ids.sourceId,
        paint: {
          'circle-radius': dotRadius,
          'circle-color': LOT_PIN_STYLE.color,
          'circle-stroke-color': LOT_PIN_STYLE.strokeColor,
          'circle-stroke-width': isOutline ? 2 : LOT_PIN_STYLE.strokeWidth,
          'circle-opacity': [
            'interpolate', ['linear'], ['zoom'],
            FADE_START_ZOOM, isOutline ? 0.55 : LOT_PIN_STYLE.circleOpacity,
            FADE_END_ZOOM, 0,
          ],
          'circle-stroke-opacity': [
            'interpolate', ['linear'], ['zoom'],
            FADE_START_ZOOM, 0.95,
            FADE_END_ZOOM, 0,
          ],
        },
      });
      // Puntos de interés encima de parcelas; pines de lista al tope (como CAD).
      moveReferencePlacesForeground(map);
      try {
        if (map.getLayer(ids.layerDotsId)) map.moveLayer(ids.layerDotsId);
      } catch (_) { /* estilo a medias */ }
    };

    const cancelWait = whenMapStyleReady(map, upsertLayers);

    const handleStyleData = () => {
      if (!map.isStyleLoaded()) return;
      upsertLayers();
    };

    map.on('styledata', handleStyleData);

    return () => {
      cancelWait();
      try {
        map.off('styledata', handleStyleData);
      } catch (_) {
        // mapa destruyéndose
      }
    };
  }, [mapInstanceRef, enabled, lots, idPrefix, variant, mapAttachVersion]);

  useEffect(() => {
    if (!enabled) {
      removeLayers(mapRef.current, idPrefix);
    }
  }, [enabled, idPrefix]);

  useEffect(() => {
    return () => removeLayers(mapRef.current, idPrefix);
  }, [idPrefix]);
}

function buildGeoJSON(lots) {
  const features = (lots || [])
    .map((l) => {
      const rep = getLotRepresentativeLatLng(l);
      if (!rep) return null;
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [rep.lng, rep.lat],
        },
        properties: {
          lot_id: l.lot_id || l.id,
          title: l.title || l.lot_name || '',
        },
      };
    })
    .filter(Boolean);

  return { type: 'FeatureCollection', features };
}

function getIds(prefix) {
  const safe = String(prefix || DEFAULT_PREFIX);
  return {
    sourceId: `${safe}-source`,
    layerHalosId: `${safe}-halos`,
    layerDotsId: `${safe}-dots`,
  };
}

function removeLayers(map, idPrefix) {
  if (!map) return;
  const ids = getIds(idPrefix);
  try {
    if (map.getLayer(ids.layerDotsId)) map.removeLayer(ids.layerDotsId);
    if (map.getLayer(ids.layerHalosId)) map.removeLayer(ids.layerHalosId);
    if (map.getSource(ids.sourceId)) map.removeSource(ids.sourceId);
  } catch (_) {
    // Mapa pudiendo destruirse
  }
}
